/**
 * Detalhe do arquivo: revisão humana e conciliação.
 *
 * A revisão aqui não é conferir linha a linha — isso a aritmética já fez. É
 * confirmar que os totais batem com o que se esperava receber e liberar o
 * arquivo para alimentar a conciliação.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { conciliarArquivo, confirmarArquivo, detalheArquivo } from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Painel,
  StatusArquivoBadge,
} from '../components/common';
import { brl, formatDateTime, numero } from '../utils/formato';

export default function ArquivoDetalhe() {
  const { id } = useParams();
  const [arquivo, setArquivo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [acaoEmCurso, setAcaoEmCurso] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true);
    detalheArquivo(id)
      .then((r) => setArquivo(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar o arquivo.')))
      .finally(() => setCarregando(false));
  }, [id]);

  useEffect(carregar, [carregar]);

  const confirmar = async () => {
    setErro('');
    setAcaoEmCurso('confirmar');
    try {
      const { data } = await confirmarArquivo(id);
      setArquivo(data);
      setAviso('Arquivo confirmado. Agora ele alimenta a conciliação.');
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível confirmar o arquivo.'));
    } finally {
      setAcaoEmCurso('');
    }
  };

  const conciliar = async () => {
    setErro('');
    setAviso('');
    setAcaoEmCurso('conciliar');
    try {
      const { data } = await conciliarArquivo(id);
      if (data.erro) {
        setAviso(
          `Bioslab indisponível: ${data.erro} As ${numero(data.pendentes)} guia(s) ficaram em ` +
            'pendência e podem ser reprocessadas — nada se perdeu.'
        );
      } else {
        setAviso(
          `Conciliação concluída: ${numero(data.resolvidas)} guia(s) resolvidas, ` +
            `${numero(data.pendentes)} pendente(s), ${numero(data.ja_conhecidas)} já conhecida(s).`
        );
      }
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível conciliar.'));
    } finally {
      setAcaoEmCurso('');
    }
  };

  if (carregando) return <LoadingSpinner />;
  if (!arquivo) return <Alerta tipo="erro">{erro || 'Arquivo não encontrado.'}</Alerta>;

  const validacoes = arquivo.validacoes || [];
  const falhas = validacoes.filter((v) => !v.passou);

  return (
    <div style={{ maxWidth: 980 }}>
      <PageHeader
        titulo={arquivo.nome_original}
        subtitulo={`Competência ${arquivo.competencia || '—'} · recebido em ${formatDateTime(arquivo.created_at)}`}
        acoes={
          <Link to="/arquivos" className="ff-btn ff-btn--secundario">
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Voltar
          </Link>
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="info" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
        <StatusArquivoBadge status={arquivo.status} />
        {arquivo.extracao_aprovada ? (
          <Badge tom="confirmado">Conferido aritmeticamente</Badge>
        ) : (
          <Badge tom="reprovado">Não conferido</Badge>
        )}
      </div>

      <div className="ff-grid-tiles mb-4">
        <Indicador natureza="neutro" rotulo="Linhas" valor={numero(arquivo.qtd_linhas)} />
        <Indicador natureza="neutro" rotulo="Guias" valor={numero(arquivo.qtd_guias)} />
        <Indicador
          natureza="financeiro"
          rotulo="Total extraído"
          valor={brl(arquivo.total_extraido)}
        />
        <Indicador
          natureza="entrada"
          rotulo="Total no documento"
          valor={brl(arquivo.total_pago_impresso)}
        />
      </div>

      <div className="mb-4">
        <Painel titulo="Conferências aritméticas">
          <ul className="list-unstyled m-0">
            {validacoes.map((v) => (
              <li
                key={v.regra}
                className="d-flex gap-3 px-4 py-3"
                style={{ borderBottom: '1px solid var(--ff-border-soft)' }}
              >
                <i
                  className={`bi ${v.passou ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}
                  style={{ color: v.passou ? '#2fa36c' : '#d92d20', fontSize: 16, marginTop: 2 }}
                  aria-hidden="true"
                />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{v.descricao || v.regra}</div>
                  {v.detalhe && (
                    <div style={{ fontSize: 13, color: 'var(--ff-muted)' }}>{v.detalhe}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Painel>
      </div>

      {falhas.length > 0 && (
        <Alerta tipo="erro">
          <strong>Erro de identificação do relatório.</strong> Este arquivo não pode ser
          confirmado e seus dados não entraram na conciliação. Um administrador precisa decidir:
          ajustar a especificação do modelo, pedir outro arquivo, ou aprovar o modelo sem
          validação aritmética.
        </Alerta>
      )}

      <div className="d-flex flex-wrap gap-2">
        {arquivo.status === 'em_revisao' && (
          <Botao variante="confirmar" onClick={confirmar} disabled={!!acaoEmCurso}>
            <i className="bi bi-check2" aria-hidden="true" />
            {acaoEmCurso === 'confirmar' ? 'Confirmando…' : 'Confirmar revisão'}
          </Botao>
        )}
        {arquivo.status === 'confirmado' && (
          <Botao variante="primario" onClick={conciliar} disabled={!!acaoEmCurso}>
            <i className="bi bi-diagram-2" aria-hidden="true" />
            {acaoEmCurso === 'conciliar' ? 'Conciliando…' : 'Resolver protocolos no Bioslab'}
          </Botao>
        )}
      </div>

      {arquivo.observacao && (
        <p style={{ fontSize: 13, color: 'var(--ff-muted)', marginTop: 16 }}>
          {arquivo.observacao}
        </p>
      )}
    </div>
  );
}
